import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';

export class DFS extends PathFinder {
  readonly name = 'Depth-First Search';
  readonly description = 'Explores as far as possible along each branch before backtracking';

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    _options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();

    grid.reset();

    const stack: Cell[] = [];
    const visited = new Set<Cell>();
    let closedCount = 0;

    start.parent = null;
    stack.push(start);
    visited.add(start);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: 0,
      openListSize: stack.length,
      closedListSize: closedCount
    };

    let nodesExplored = 0;

    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          openListSize: stack.length,
          closedListSize: closedCount
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      current.state = CellState.CLOSED;
      closedCount++;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        openListSize: stack.length,
        closedListSize: closedCount
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          neighbor.parent = current;
          stack.push(neighbor);
          visited.add(neighbor);
          neighbor.state = CellState.OPEN;

          yield {
            type: 'open',
            cell: neighbor,
            parent: current,
            openListSize: stack.length,
            closedListSize: closedCount
          };
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }
}
